import { APIService, APIError } from '../../../lib/api/APIService';

export const GENERATION_FAILED_MESSAGE = "Drafting didn't finish. Try again.";

function abortError() {
  return new DOMException('Aborted', 'AbortError');
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw abortError();
}

// setTimeout that rejects as soon as signal aborts, so an unmount or a
// session change ends the wait instead of polling once more.
function abortableDelay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener?.('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener?.('abort', onAbort, { once: true });
  });
}

// Paths API: Path lifecycle, planning sessions (chat-based creation),
// generation, plan-item execution. Modelled on taskListAPI.js's
// class+singleton shape. The SSE parsing in postMessageStream/generate
// mirrors chatAPI.postMessageStream exactly (same event vocabulary,
// same buffering logic) — duplicated rather than imported because
// chatAPI's method hardcodes the /api/chat/... endpoint path.
class PathsAPI extends APIService {
  // --- Path lifecycle -------------------------------------------------
  async listPaths() {
    return this.get('/api/paths');
  }

  async getPath(pathId) {
    return this.get(`/api/paths/${pathId}`);
  }

  async updatePathStatus(pathId, status) {
    return this.patch(`/api/paths/${pathId}`, { status });
  }

  async getNextAction(pathId) {
    return this.get(`/api/paths/${pathId}/next-action`);
  }

  async acceptVersion(pathId, versionNumber) {
    return this.post(`/api/paths/${pathId}/versions/${versionNumber}/accept`);
  }

  async discardVersion(pathId, versionNumber) {
    return this.post(`/api/paths/${pathId}/versions/${versionNumber}/discard`);
  }

  // --- Plan-item execution --------------------------------------------
  async updateItem(itemId, status, note = null) {
    const body = { status };
    if (note) body.note = note;
    return this.patch(`/api/paths/items/${itemId}`, body);
  }

  // --- Review checkpoints (W2.2) ---------------------------------------
  async getReviewPreview(itemId) {
    return this.get(`/api/paths/items/${itemId}/review-preview`);
  }

  async submitReview(itemId, { decision, reflection, reportedOutcome } = {}) {
    const body = { decision };
    if (reflection) body.reflection = reflection;
    if (reportedOutcome) body.reported_outcome = reportedOutcome;
    return this.post(`/api/paths/items/${itemId}/review`, body);
  }

  // --- Planning sessions -----------------------------------------------
  async createSession(purpose = 'creation', pathId = null) {
    const body = { purpose };
    if (pathId) body.path_id = pathId;
    return this.post('/api/paths/sessions', body);
  }

  // Open creation sessions that have no Path yet (a planning chat before
  // its first draft), newest first — GET /api/paths cannot list these.
  async listOpenCreationSessions() {
    return this.get('/api/paths/sessions?purpose=creation&open=true');
  }

  async getSession(sessionId) {
    return this.get(`/api/paths/sessions/${sessionId}`);
  }

  async discardSession(sessionId) {
    return this.post(`/api/paths/sessions/${sessionId}/discard`);
  }

  // --- Capabilities ------------------------------------------------------
  async getCapabilities() {
    return this.get('/api/paths/capabilities');
  }

  /**
   * Stream a planning chat turn via SSE. Callbacks: onToken(delta),
   * onDone({messages, ready_to_generate}), onError(err).
   */
  async postMessageStream(sessionId, message, callbacks = {}, signal = undefined) {
    const { onToken, onDone, onError } = callbacks;

    await this.ensureAuthFresh();
    const token = this.getAccessToken();
    const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(
      `${this.baseURL}/api/paths/sessions/${sessionId}/messages/stream`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: message || '' }),
        credentials: 'include',
        signal,
      }
    );

    return this._consumeSseStream(response, { onToken, onDone, onError });
  }

  /**
   * Start draft generation (returns immediately) then poll status until
   * terminal. Works under DigitalOcean's ~60s HTTP edge limit — the LLM
   * runs in a backend thread against path_generation_attempts.
   * Callbacks: onPhase(phase), onDone({path_id, version_number}),
   * onError(err), onStale(status).
   */
  async generate(sessionId, callbacks = {}, signal = undefined) {
    const { onPhase, onError } = callbacks;

    try {
      const started = await this.post(`/api/paths/sessions/${sessionId}/generate`);
      throwIfAborted(signal);
      callbacks.onStarted?.(started);
      if (started?.phase) onPhase?.(started.phase);
    } catch (e) {
      if (e?.name !== 'AbortError') onError?.(e);
      throw e;
    }
    return this._pollGenerationStatus(sessionId, callbacks, signal);
  }

  /**
   * Start revision generation then poll the same status endpoint.
   * Callbacks as for generate.
   */
  async updateDraft(sessionId, callbacks = {}, signal = undefined) {
    const { onPhase, onError } = callbacks;
    try {
      const started = await this.post(`/api/paths/sessions/${sessionId}/draft-update`, {});
      throwIfAborted(signal);
      callbacks.onStarted?.(started);
      if (started?.phase) onPhase?.(started.phase);
    } catch (e) {
      if (e?.name !== 'AbortError') onError?.(e);
      throw e;
    }
    return this._pollGenerationStatus(sessionId, callbacks, signal);
  }

  async generateRevision(pathId, sessionId, callbacks = {}, signal = undefined) {
    const { onPhase, onError } = callbacks;

    try {
      const started = await this.post(`/api/paths/${pathId}/revisions`, { session_id: sessionId });
      throwIfAborted(signal);
      callbacks.onStarted?.(started);
      if (started?.phase) onPhase?.(started.phase);
    } catch (e) {
      if (e?.name !== 'AbortError') onError?.(e);
      throw e;
    }
    return this._pollGenerationStatus(sessionId, callbacks, signal);
  }

  /**
   * Follow a generation that is already running (after a reload) without
   * starting one: the same polling as generate, with no POST. Callbacks as
   * for generate.
   */
  async resumeGeneration(sessionId, callbacks = {}, signal = undefined) {
    return this._pollGenerationStatus(sessionId, callbacks, signal);
  }

  // Polls until the attempt finishes. The server says when an attempt is
  // stale (older than its stuck threshold, so it cannot finish): that
  // resolves with {stale: true} after onStale, instead of polling forever.
  // A failure rejects with an APIError carrying only the reason code; the
  // server sends no error text. APIService cannot cancel a GET in flight,
  // so the signal is checked after every GET and during the wait between.
  async _pollGenerationStatus(sessionId, { onPhase, onDone, onError, onStale, onStatus } = {}, signal) {
    const pollMs = 2000;
    // A backstop only: the server's own deadline and stale flag end an
    // attempt long before this (path_generation_service: 14 + 2 minutes).
    const maxWaitMs = 20 * 60 * 1000;
    const startedAt = Date.now();
    let lastPhase = null;

    while (true) {
      throwIfAborted(signal);
      if (Date.now() - startedAt > maxWaitMs) {
        const err = new APIError(GENERATION_FAILED_MESSAGE, 504, { reason: 'timed_out' });
        onError?.(err);
        throw err;
      }

      const status = await this.get(`/api/paths/sessions/${sessionId}/generate/status`);
      throwIfAborted(signal);
      onStatus?.(status);
      if (status?.phase && status.phase !== lastPhase) {
        lastPhase = status.phase;
        onPhase?.(status.phase);
      }

      if (status?.status === 'succeeded') {
        const donePayload = {
          path_id: status.path_id,
          version_number: status.version_number,
        };
        onDone?.(donePayload);
        return donePayload;
      }

      if (status?.status === 'failed' || status?.status === 'invalid_output' || status?.status === 'idle') {
        const err = new APIError(GENERATION_FAILED_MESSAGE, 400, { reason: status?.reason || null });
        onError?.(err);
        throw err;
      }

      if (status?.status === 'in_progress' && status.stale) {
        onStale?.(status);
        return { stale: true };
      }

      await abortableDelay(pollMs, signal);
    }
  }

  /** Shared SSE reader — mirrors chatAPI.postMessageStream's buffering
   * logic exactly (split on double-newline, decode incrementally). */
  async _consumeSseStream(response, { onToken, onPhase, onDone, onError }) {
    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
      let data = {};
      if (contentType.includes('application/json')) {
        data = await response.json().catch(() => ({}));
      } else {
        const text = await response.text().catch(() => '');
        if (text) data = { message: text };
      }
      const err = new APIError(
        data.error || data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data
      );
      onError?.(err);
      throw err;
    }

    if (!response.body || !contentType.includes('text/event-stream')) {
      const err = new APIError('Streaming not available', 500, {});
      onError?.(err);
      throw err;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let donePayload = null;

    const parseSseBlock = (block) => {
      let eventType = 'message';
      let dataStr = '';
      block.split('\n').forEach((line) => {
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataStr += line.slice(5).trim();
        }
      });
      if (!dataStr) return;
      let data;
      try {
        data = JSON.parse(dataStr);
      } catch {
        data = { raw: dataStr };
      }
      if (eventType === 'token' && data.delta) {
        onToken?.(data.delta);
      } else if (eventType === 'phase') {
        onPhase?.(data.phase);
      } else if (eventType === 'ready') {
        // Connection alive — no UI action required.
      } else if (eventType === 'done') {
        donePayload = data;
        onDone?.(data);
      } else if (eventType === 'error') {
        const err = new APIError(data.message || data.error || 'Stream failed', 400, data);
        onError?.(err);
        throw err;
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        parts.forEach(parseSseBlock);
      }
      if (buffer.trim()) {
        parseSseBlock(buffer);
      }
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
      if (!(e instanceof APIError)) onError?.(e);
      throw e;
    }

    if (!donePayload) {
      const err = new APIError('Stream ended without completion', 500, {});
      onError?.(err);
      throw err;
    }

    return donePayload;
  }
}

export const pathsAPI = new PathsAPI();
